import api from '@/lib/api'
import { ENDPOINTS } from '@/constants/endpoints'
import { IndividualType } from '@/constants/account'
import type { User, Setup2FAResponse, Enable2FAResponse } from '@/types/user'

export interface VerifyRppsRequest {
  rpps: string
  promo_code?: string
}

export interface VerifyRppsResponse {
  rpps: string
  promo_code?: string | null
  promo_code_error?: string | null
  promo_code_free_months?: number | null
  data: {
    id_pp: string
    nom_exercice: string
    prenom_exercice: string
    profession: string
    categorie_pro: string
    exe_pro_id: string
    label_typ_id_pp: string
    mode_exercice: string
    statut_activite: string
    activite_id: string
    num_activite: string
    fonction: string | null
    structure: string | null
    id_structure: string | null
    commune: string | null
    specialites: string[]
    bal_msses: string[]
    situexes: Array<{
      situExeId: string
      idPm: string | null
      raisonSociale: string | null
      commune: string | null
      codePostal: string | null
    }>
    etat_exe_pro: string
    danger: boolean
  }
}

export interface VerifyRegistrationCodeRequest {
  code: string
}

export interface VerifyRegistrationCodeResponse {
  valid: boolean
}

export interface CreateUserRequest {
  email: string
  password: string
  stripe_subscription_price?: string
  rpps?: string
  registration_code?: string
  country?: string
  individual_type?: IndividualType
  affiliated_org?: string
  finess_number?: string
  first_name?: string
  last_name?: string
  payment_type?: 'internal' | 'external'
  facility_name?: string
  additional_data?: Record<string, unknown>
  custom_price?: boolean
  promo_code?: string
}

export interface CreateUserResponse {
  message?: string
  promo_code_error?: string | null
}

export interface VerifyEmailRequest {
  email: string
  code: string
  customer_type?: 'user' | 'organization'
  payment_type?: 'internal' | 'external' | 'custom'
}

export interface VerifyEmailResponse {
  stripe_payment_url?: string
  access_token?: string
  refresh_token?: string
  organization_id?: string
}

export interface VerifyOrgEmailRequest {
  email: string
  code: string
}

export interface VerifyOrgEmailResponse {
  access_token: string
  refresh_token: string
  organization_id: string
}

export interface PasswordResetRequest {
  email: string
}

export interface PasswordResetVerifyRequest {
  email: string
  code: string
}

export interface PasswordResetConfirmRequest {
  email: string
  code: string
  new_password: string
}

export interface UpdateUserRequest {
  username?: string
  email?: string | null
  first_name?: string
  last_name?: string
  password?: string
}

export interface ChangePasswordRequest {
  current_password: string
  new_password: string
}

export const usersService = {
  getMe: async (): Promise<User> => {
    const response = await api.get<User>(ENDPOINTS.USERS.ME)
    return response.data
  },

  updateUser: async (id: string, data: UpdateUserRequest): Promise<User> => {
    const response = await api.patch<User>(ENDPOINTS.USERS.UPDATE(id), data)
    return response.data
  },

  changePassword: async (data: ChangePasswordRequest): Promise<void> => {
    await api.post(ENDPOINTS.USERS.CHANGE_PASSWORD, data)
  },

  verifyRpps: async (data: VerifyRppsRequest): Promise<VerifyRppsResponse> => {
    const response = await api.post<VerifyRppsResponse>(ENDPOINTS.USERS.VERIFY_RPPS, data)
    return response.data
  },

  verifyRegistrationCode: async (
    data: VerifyRegistrationCodeRequest
  ): Promise<VerifyRegistrationCodeResponse> => {
    const response = await api.post<VerifyRegistrationCodeResponse>(
      ENDPOINTS.USERS.VERIFY_REGISTRATION_CODE,
      data
    )
    return response.data
  },

  createUser: async (data: CreateUserRequest): Promise<CreateUserResponse> => {
    const response = await api.post<CreateUserResponse>(ENDPOINTS.USERS.CREATE, data)
    return response.data
  },

  verifyEmail: async (data: VerifyEmailRequest): Promise<VerifyEmailResponse> => {
    const response = await api.post<VerifyEmailResponse>(ENDPOINTS.USERS.VERIFY_EMAIL, data)
    return response.data
  },

  verifyOrgEmail: async (data: VerifyOrgEmailRequest): Promise<VerifyOrgEmailResponse> => {
    const response = await api.post<VerifyOrgEmailResponse>(ENDPOINTS.USERS.VERIFY_ORG_EMAIL, data)
    return response.data
  },

  requestPasswordReset: async (data: PasswordResetRequest): Promise<void> => {
    await api.post(ENDPOINTS.USERS.PASSWORD_RESET, data)
  },

  verifyPasswordResetCode: async (data: PasswordResetVerifyRequest): Promise<void> => {
    await api.post(ENDPOINTS.USERS.PASSWORD_RESET_VERIFY, data)
  },

  confirmPasswordReset: async (data: PasswordResetConfirmRequest): Promise<void> => {
    await api.post(ENDPOINTS.USERS.PASSWORD_RESET_CONFIRM, data)
  },

  resendVerificationEmail: async (email: string): Promise<void> => {
    await api.post(ENDPOINTS.USERS.RESEND_VERIFICATION_EMAIL, { email })
  },

  get2FASetup: async (): Promise<Setup2FAResponse> => {
    const response = await api.get<Setup2FAResponse>(ENDPOINTS.USERS.TWO_FA_SETUP)
    return response.data
  },

  enable2FA: async (totpCode: string): Promise<Enable2FAResponse> => {
    const response = await api.post<Enable2FAResponse>(ENDPOINTS.USERS.TWO_FA_ENABLE, {
      totp_code: totpCode.trim(),
    })
    return response.data
  },

  disable2FA: async (): Promise<void> => {
    await api.post(ENDPOINTS.USERS.TWO_FA_DISABLE, {})
  },
}
